<script setup lang="ts">
import 'leaflet/dist/leaflet.css'
import { createLeafletLocationMapAdapter } from '~/shared-kernel/infrastructure/location/leaflet-location-map.adapter'
import type { LocationCoordinates } from '~/shared-kernel/domain/location'
import { isValidLocationCoordinates } from '~/shared-kernel/domain/location'
import type { LocationMapAdapter } from '~/shared-kernel/application/ports/location-map-adapter'

const props = defineProps<{ location: LocationCoordinates; label: string; errorLabel: string }>()
const target = ref<HTMLElement | null>(null)
const failed = ref(false)
let adapter: LocationMapAdapter | undefined
let observer: ResizeObserver | undefined
let disposed = false

function value() {
  return { ...props.location, address: '', mapProvider: 'openstreetmap' }
}

onMounted(async () => {
  if (!target.value || !isValidLocationCoordinates(props.location)) return
  adapter = createLeafletLocationMapAdapter()
  try {
    await adapter.mount(target.value, value())
    if (disposed) return
    adapter.setValue(value())
    await nextTick()
    if (disposed) return
    adapter?.resize()
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => adapter?.resize())
      if (target.value) observer.observe(target.value)
    }
  } catch {
    if (!disposed) failed.value = true
    adapter?.destroy()
  }
})
watch(() => [props.location.latitude, props.location.longitude], () => {
  if (isValidLocationCoordinates(props.location)) adapter?.setValue(value())
})
onBeforeUnmount(() => {
  disposed = true
  observer?.disconnect()
  adapter?.destroy()
  adapter = undefined
})
</script>

<template>
  <div class="location-map">
    <p v-if="failed" role="status">{{ errorLabel }}</p>
    <div v-else ref="target" class="location-map__canvas" role="region" :aria-label="label" />
  </div>
</template>

<style scoped>
.location-map { overflow: hidden; border: 1px solid var(--tq-border); border-radius: var(--tq-radius-md); }
.location-map__canvas { height: 20rem; width: 100%; }
.location-map p { padding: 1rem; }
</style>
